import { useEffect, useState } from "react";
import { useLang } from "@/contexts/LangContext";
import { api, formatSAR } from "@/lib/api";
import { Users, DollarSign, UserPlus, Crown, BadgePercent, Calendar } from "lucide-react";
import { XCircle } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line,
} from "recharts";

const CARD = (Icon, key, value, color) => ({ Icon, key, value, color });

// Helper: local-Y-M-D so date default = Riyadh today (not UTC today)
function localIso(d = new Date()) {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export default function DashboardPage() {
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(localIso());

  useEffect(() => {
    setLoading(true);
    const params = date === localIso() ? {} : { date };
    api.get("/dashboard/summary", { params })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [date]);

  const isToday = date === localIso();

  if (loading && !data) return <div className="text-foreground/60">{t("common.loading")}</div>;
  if (!data) return null;

  const cards = [
    CARD(Users, "dash.visits", data.visits, "gold"),
    CARD(DollarSign, "dash.revenue", formatSAR(data.revenue), "gold"),
    CARD(UserPlus, "dash.newCustomers", data.new_customers, "leaf"),
    CARD(Crown, "dash.vip", data.vip_visits, "gold"),
    CARD(BadgePercent, "dash.discounted", data.discounted, "leaf"),
    CARD(XCircle, "dash.cancelled", data.cancelled ?? 0, "red"),
  ];

  return (
    <div className="space-y-10">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <span className="text-xs uppercase tracking-[0.3em] text-gold">— Dashboard</span>
          <h1 className="text-3xl md:text-4xl font-heading mt-2">{t("nav.dashboard")}</h1>
          <p className="text-foreground/50 mt-1">
            {new Date(date + "T00:00:00").toLocaleDateString(lang === "ar" ? "ar-SA" : lang === "id" ? "id-ID" : "en-GB", {
              weekday: "long", year: "numeric", month: "long", day: "numeric",
            })}
            {!isToday && <span className="ms-2 text-[10px] uppercase tracking-[0.2em] bg-gold/15 text-gold rounded-full px-2 py-0.5">Historis</span>}
          </p>
        </div>
        <div className="flex items-center gap-2 bg-charcoal-900 border border-gold/20 rounded-lg px-3 py-2" data-testid="dashboard-date-picker">
          <Calendar className="h-4 w-4 text-gold" />
          <input
            type="date"
            value={date}
            max={localIso()}
            onChange={(e) => setDate(e.target.value)}
            data-testid="dashboard-date-input"
            className="bg-transparent outline-none text-sm"
          />
          {!isToday && (
            <button
              onClick={() => setDate(localIso())}
              data-testid="dashboard-today-btn"
              className="text-[10px] uppercase tracking-[0.2em] text-leaf hover:text-leaf-light border border-leaf/30 rounded-full px-2 py-0.5"
            >
              Hari Ini
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 md:gap-4">
        {cards.map((c, i) => {
          const isRed = c.color === "red";
          const isGold = c.color === "gold";
          const accentCls = isRed ? "" : (isGold ? "accent-gold" : "accent-leaf");
          const iconCls = isRed
            ? "bg-red-500/15 text-red-400"
            : (isGold ? "bg-gold/15 text-gold" : "bg-leaf/15 text-leaf");
          const valueCls = isRed ? "text-red-400" : (c.color === "leaf" ? "text-leaf" : "");
          return (
            <div
              key={c.key}
              className={`card-luxury ${accentCls} ${isRed ? "ring-1 ring-red-500/20" : ""} p-5`}
              data-testid={`summary-card-${c.key.replace("dash.", "")}`}
            >
              <div className="flex items-center justify-between">
                <div className={`h-10 w-10 rounded-xl ${iconCls} grid place-items-center`}>
                  <c.Icon className="h-5 w-5" />
                </div>
              </div>
              <div className="mt-4 text-xs uppercase tracking-[0.2em] text-foreground/50">{t(c.key)}</div>
              <div className={`mt-1 text-2xl md:text-3xl font-heading font-light ${valueCls}`}>{c.value}</div>
              {c.key === "dash.cancelled" && data.refund_amount > 0 && (
                <div className="text-[10px] text-red-300/70 mt-1">
                  −{formatSAR(data.refund_amount)} {t("dash.refundAmount")}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="card-luxury p-6 lg:col-span-2" data-testid="revenue-chart">
          <h2 className="text-lg font-heading mb-4">{t("dash.revenue7d")}</h2>
          <div className="h-72 min-h-[288px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.revenue_7d}>
                <CartesianGrid strokeDasharray="3 3" stroke="#3d3d3d" />
                <XAxis dataKey="date" stroke="#75756c" tickFormatter={(d) => d.slice(5)} />
                <YAxis stroke="#75756c" />
                <Tooltip
                  contentStyle={{ background: "#1f1f1f", border: "1px solid #3d3d3d", borderRadius: 8 }}
                  formatter={(v) => formatSAR(v)}
                />
                <Line type="monotone" dataKey="revenue" stroke="#D4AF37" strokeWidth={2.5} dot={{ fill: "#D4AF37", r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="card-luxury p-6" data-testid="top-services-chart">
          <h2 className="text-lg font-heading mb-4">{t("dash.topServices")}</h2>
          <div className="h-72 min-h-[288px] w-full">
            {data.top_services.length === 0 ? (
              <div className="h-full grid place-items-center text-foreground/40">{t("common.empty")}</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.top_services} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#3d3d3d" />
                  <XAxis type="number" stroke="#75756c" />
                  <YAxis type="category" dataKey="name" stroke="#75756c" width={110} tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: "#1f1f1f", border: "1px solid #3d3d3d" }} />
                  <Bar dataKey="count" fill="#6DBE45" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
